import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Section } from '../Section';
import { DEMO_SCRIPTS, TAG_TONE } from '../../data/howDemo';
function XMark() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-207" data-mp="30aeaq1syt0yx">
      <path d="M18 6 6 18M6 6l12 12" data-id="element-208" data-mp="349m142ovxbd6" />
    </svg>;
}
function Check() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-209" data-mp="27n0mcs3c1dqf">
      <path d="M20 6 9 17l-5-5" data-id="element-210" data-mp="2glvui9pxsxwd" />
    </svg>;
}

/**
 * Section 02 plus the demo window beneath it. Loads on the ungoverned draft;
 * the red button swaps the whole window to the governed one. No animation.
 */
export function TryIt() {
  const [idx, setIdx] = useState(0);
  const [governed, setGoverned] = useState(false);
  const s = DEMO_SCRIPTS[idx];
  const pick = (i: number) => {
    setIdx(i);
    setGoverned(false);
  };
  return <>
      <Section n="02" label="Try it" className="tryit" data-id="element-211" data-mp="ujqtkh08jckt">
        <h2 className="h2" data-id="element-212" data-mp="pjm8sesanvos">Same prompt. Two outcomes.</h2>
        <p className="lede" data-id="element-213" data-mp="krupmpbgws8o">
          The difference is what the model is told, before a single token is spent. Here are some examples of AI output based on prompts
          for different use-cases. Review the basic output, then click “Add instruction data” to see the difference Synthia makes.
        </p>
        <div className="tabs" role="group" aria-label="Choose a scenario" data-id="element-214" data-mp="6sbhvxmkjiww">
          <span className="tabs-l" data-id="element-215" data-mp="6ufyng5kgcel">TRY IT:</span>
          {DEMO_SCRIPTS.map((d, i) => <button key={d.id} type="button" className={`tab${i === idx ? ' on' : ''}`} aria-pressed={i === idx} onClick={() => pick(i)} data-id="element-216" data-mp="1v31a1h2jmxr" data-mpi={i}>
              {d.tab}
            </button>)}
        </div>
      </Section>

      <div className="demo" data-id="element-217" data-mp="1r7wzr8ct2d17">
        <div className="demo-src" data-id="element-218" data-mp="1r7n71d0dlrrt">{s.source}</div>
        <div className="win" data-id="element-219" data-mp="1r5u52b0pjvq5">
          <div className="win-bar" data-id="element-220" data-mp="3lnlog5ior7gf">
            <div className="dots" aria-hidden="true" data-id="element-221" data-mp="3lndd4i27oa7c">
              <i data-id="element-222" data-mp="3qelm5czmptjz" />
              <i data-id="element-223" data-mp="3qf3o8q8z31zt" />
              <i data-id="element-224" data-mp="3qfn7caaczwff" />
            </div>
            <div className="win-url" data-id="element-225" data-mp="3qgol4mirex0u">
              app.meetsynthia.ai<span data-id="element-226" data-mp="3bra9t8vmi5it">/</span>
              {s.workflow}
            </div>
            <div className="win-live" data-id="element-227" data-mp="3goisrbolidl4">
              <i aria-hidden="true" data-id="element-228" data-mp="3gpk47h9ix23d" />
              LIVE
            </div>
          </div>

          <div className="win-ask" data-id="element-229" data-mp="8ajxkc9g6w8o">
            <div className="who" data-id="element-230" data-mp="87ea11ggw2f2">{s.who}</div>
            <div className="q" data-id="element-231" data-mp="d7aef1t2tvv5">{s.prompt}</div>
            {!governed && <button type="button" className="send" onClick={() => setGoverned(true)} data-id="element-232" data-mp="3ugp6ys86uahx">
                {s.send}
              </button>}
          </div>

          <div aria-live="polite" data-id="element-233" data-mp="3c8o42n2enqz">
            {!governed ? <>
                <div className="win-g" data-id="element-234" data-mp="2izqo9ibk0fdn">
                  <div className="win-l" data-id="element-235" data-mp="2o04v4x1f5ja3">
                    <div className="win-h" data-id="element-236" data-mp="2o165ezam21qr">WITHOUT SYNTHIA</div>
                    <div className="draft" data-id="element-237" data-mp="1628j3oo9pof1">
                      {s.without.map((seg, i) => seg.bad ? <mark key={i} className="bad" data-id="element-238" data-mp="112vm4b3on0yq">
                            {seg.x}
                          </mark> : <span key={i} data-id="element-239" data-mp="1b24c6svffvft">{seg.x}</span>)}
                    </div>
                  </div>
                  <div className="win-r" data-id="element-240" data-mp="ncs3rv7xbrek">
                    <div className="win-h" data-id="element-241" data-mp="id5ufwv7uvb1">WHAT IT SKIPPED</div>
                    {s.skipped.map(t => <div className="xrow" key={t} data-id="element-242" data-mp="w4c0kbt03r6w">
                        <XMark data-id="element-243" data-mp="w5ve3un9vo4z" />
                        <span data-id="element-244" data-mp="rdvlkn9j7xif">{t}</span>
                      </div>)}
                  </div>
                </div>
                <div className="strip red" data-id="element-245" data-mp="3n7xosj80xa8">
                  <span data-id="element-246" data-mp="3m6l2jmk42qe">{s.missed}</span>
                </div>
              </> : <>
                <div className="win-g" data-id="element-247" data-mp="cbx4vyowkno1">
                  <div className="win-l" data-id="element-248" data-mp="ccyedyq9t23n">
                    <div className="win-h" data-id="element-249" data-mp="c9u72shidq8r">
                      <i className="gdot" aria-hidden="true" data-id="element-250" data-mp="ha8fibn0v27b" />
                      WITH SYNTHIA
                    </div>
                    <div className="draft" data-id="element-251" data-mp="m9baf39afs1k">
                      {s.with.map((p, i) => <p key={i} data-id="element-252" data-mp="r1l6z24wrqct" data-mpi={i}>{p}</p>)}
                    </div>
                  </div>
                  <div className="win-r told" data-id="element-253" data-mp="vzcznyddngi2">
                    <div className="win-h" data-id="element-254" data-mp="1017xgsw3nbr3">WHAT THE MODEL WAS TOLD</div>
                    {s.told.map(t => <div className="crow" key={t.rule} data-id="element-255" data-mp="150b1qb499dy0">
                        <Check data-id="element-256" data-mp="14yrm8mqfb6xa" />
                        <span className="tag" style={{
                    color: TAG_TONE[t.tag],
                    borderColor: TAG_TONE[t.tag]
                  }} data-id="element-257" data-mp="1a076icks19em">
                          {t.tag}
                        </span>
                        <span className="r" data-id="element-258" data-mp="3br0t3jt6mqw4">{t.rule}</span>
                      </div>)}
                    <div className="mini-rcpt" data-id="element-259" data-mp="3479ei4g1d3n0">
                      <div className="rh" data-id="element-260" data-mp="3492jzgewxhtf">
                        <span data-id="element-261" data-mp="2zgsx6993gxvd">/ INSTRUCTION RECEIPT</span>
                        <span data-id="element-262" data-mp="2zf81uheyhawg">{s.receiptId}</span>
                      </div>
                      <div className="kv bb" data-id="element-263" data-mp="2uirgtw31zcnl">
                        <span className="k" data-id="element-264" data-mp="2ufdciiatwtdi">rules</span>
                        <span style={{
                      color: 'var(--green)',
                      fontWeight: 500
                    }} data-id="element-265" data-mp="2pjy260c15flu">{s.told.length}/{s.told.length} included</span>
                        <span className="k" data-id="element-266" data-mp="3n4awcpuouy8d">workflow</span>
                        <span data-id="element-267" data-mp="3i8c1qkirojzi">{s.workflow}</span>
                        <span className="k" data-id="element-268" data-mp="3i5g3ev30btaj">source</span>
                        <span data-id="element-269" data-mp="3d764c9e5w8jn">{s.source}</span>
                        <span className="k" data-id="element-270" data-mp="3da0soc0aqdic">passes</span>
                        <span data-id="element-271" data-mp="388bg2rxfdrs5">{s.passes}</span>
                      </div>
                      <div className="kv" data-id="element-272" data-mp="21tkaiaxcxirs">
                        <span className="k" data-id="element-273" data-mp="21uc63lqyfgi2">algorithm</span>
                        <span data-id="element-274" data-mp="1wtodivn2urlq">ed25519</span>
                        <span className="k" data-id="element-275" data-mp="1ww28l4hrc3ta">digest</span>
                        <span className="ell" data-id="element-276" data-mp="ou8uepsfdlfd">{s.digest}</span>
                        <span className="k" data-id="element-277" data-mp="sw3whwi6k2u7">2026-09-24</span>
                        <span className="sig" data-id="element-278" data-mp="svkfqj49nkhr">
                          <span data-id="element-279" data-mp="ewl0u36k2jys">14:02:11 UTC</span>
                          <span className={`signed${s.stamp === 'HELD' ? ' held' : ''}`} data-id="element-280" data-mp="evt42p182g6x">{s.stamp}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`strip ${s.stamp === 'HELD' ? 'amber' : 'green'}`} data-id="element-281" data-mp="54ophqfgkxcm">
                  <span data-id="element-282" data-mp="9zcgpx1uk5uw">{s.done}</span>
                  <button type="button" onClick={() => setGoverned(false)} data-id="element-283" data-mp="3sbr472rrd2if">
                    ← See it without
                  </button>
                </div>
              </>}
          </div>

          <div className="win-foot" data-id="element-284" data-mp="3icrt7djisus9">
            <div className="t" data-id="element-285" data-mp="3idl14hu7tqbz">Same prompt. Same model. One of these becomes Exhibit A.</div>
            <Link className="btn btn-o" to="/#atomizer" style={{
            height: 40,
            fontSize: 14,
            padding: '0 16px'
          }} data-id="element-286" data-mp="3nbmmj9f461qm">
              See it on a real document <span className="mono" data-id="element-287" data-mp="2zor4366e9wdo">↓</span>
            </Link>
          </div>
        </div>
      </div>
    </>;
}